import { Box, Paper, Typography } from "@mui/material";
import React from "react";
import { CodeBlock, monokaiSublime } from "react-code-blocks";

interface ICodeContent {
  language: string;
  code?: string;
  text?: string;
}

interface ISuggestionObject {
  description?: string;
  code: ICodeContent;
}

interface ISuggestionDisplayProps {
  suggestion: string | ISuggestionObject;
}

/**
 * A regex to find code blocks within a string.
 * It looks for:
 * 1. ``` (three backticks)
 * 2. Optionally, a language name (e.g., javascript, js, ts, csharp)
 * 3. Newline
 * 4. The code content (non-greedy match for any character including newlines)
 * 5. Newline
 * 6. ``` (three backticks)
 */
const CODE_BLOCK_REGEX = /```(\w+)?\n([\s\S]*?)\n```/;

const SuggestionDisplay: React.FC<ISuggestionDisplayProps> = ({
  suggestion,
}) => {
  if (typeof suggestion === "string") {
    const match = new RegExp(CODE_BLOCK_REGEX).exec(suggestion);

    if (match) {
      const fullMatch = match[0];
      const language = match[1] || "text";
      const code = match[2].trim();

      const parts = suggestion.split(fullMatch);
      const descriptionBefore = parts[0].trim();
      const descriptionAfter = parts[1] ? parts[1].trim() : "";

      const fullDescription = [descriptionBefore, descriptionAfter]
        .filter(Boolean)
        .join("\n\n");

      return (
        <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1, mb: 2 }}>
          {fullDescription && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, whiteSpace: "pre-wrap" }}
            >
              {fullDescription}
            </Typography>
          )}

          <Paper
            elevation={3}
            sx={{ position: "relative", overflow: "hidden" }}
          >
            {language && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  px: 1,
                  py: 0.5,
                  bgcolor: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  borderRadius: 1,
                  zIndex: 1,
                }}
              >
                {language.toUpperCase()}
              </Typography>
            )}
            <CodeBlock text={code} language={language} theme={monokaiSublime} />
          </Paper>
        </Box>
      );
    } else {
      return (
        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
          {suggestion}
        </Typography>
      );
    }
  }

  const typedSuggestion = suggestion;

  if (
    typedSuggestion?.code &&
    (typeof typedSuggestion.code.code === "string" ||
      typeof typedSuggestion.code.text === "string")
  ) {
    const { description, code } = typedSuggestion;

    return (
      <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1, mb: 2 }}>
        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, whiteSpace: "pre-wrap" }}
          >
            {description}
          </Typography>
        )}

        <Paper elevation={3} sx={{ position: "relative", overflow: "hidden" }}>
          {code.language && (
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                px: 1,
                py: 0.5,
                bgcolor: "rgba(0, 0, 0, 0.6)",
                color: "white",
                borderRadius: 1,
                zIndex: 1,
              }}
            >
              {code.language.toUpperCase()}
            </Typography>
          )}

          <CodeBlock
            text={code.code ?? code.text}
            language={code.language || "text"}
            showLineNumbers={false}
            theme={monokaiSublime}
          />
        </Paper>
      </Box>
    );
  }

  return (
    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
      Invalid suggestion format. Expected string, string with code block, or
      object with 'description' and 'code' or 'text'.
    </Typography>
  );
};

export default SuggestionDisplay;
